import type { DataMatrixCodeSizeMm, LabelElement, LabelElementType, LabelPreviewData, LabelTemplate, RotationType } from '@/types/label-template';
import { snapDataMatrixCodeSizeMm } from '@/utils/datamatrix-size';
import { bindingToToken, renderFastwmsTemplate, type FastwmsLabelData } from '@/utils/fastwms-variables';

/** Статический текст на этикетке — без привязки label.* */
export const STATIC_TEXT_FIELD_LABEL = 'Свой текст';

export function isStaticTextElement(element: Pick<LabelElement, 'type' | 'binding'>): boolean {
  return element.type === 'text' && !String(element.binding ?? '').trim();
}

/** Текст для отображения на холсте / при печати. */
export function resolveTextElementDisplay(
  element: Extract<LabelElement, { type: 'text' }>,
  previewData: FastwmsLabelData,
  options?: { showLabel?: boolean },
): string {
  const showLabel = options?.showLabel ?? element.showLabel;
  const body = isStaticTextElement(element)
    ? String(element.value ?? '')
    : renderFastwmsTemplate(element.value || bindingToToken(element.binding), previewData);
  return showLabel ? `${element.label}: ${body}` : body;
}

/** Элемент нижнего каталога полей; `group` — только режим «Склад», подзаголовки в меню добавления полей */
export type FieldCatalogSeed = {
  label: string;
  type: LabelElementType;
  binding: string;
  width: number;
  height: number;
  showLabel?: boolean;
  group?: string;
};

/** Порядок групп в панели полей для режима склада */
export const WAREHOUSE_CELL_FIELD_GROUP_ORDER = [
  'Коды',
  'Подпись и адрес',
  'Склад и единица хранения',
  'Группы хранения',
  'Дополнительно',
] as const;

export const LABEL_SIZES = [
  { widthMm: 40, heightMm: 30, title: '40x30' },
  { widthMm: 58, heightMm: 30, title: '58x30' },
  { widthMm: 58, heightMm: 40, title: '58x40' },
  { widthMm: 58, heightMm: 60, title: '58x60' },
  { widthMm: 70, heightMm: 50, title: '70x50' },
  { widthMm: 80, heightMm: 50, title: '80x50' },
  { widthMm: 100, heightMm: 100, title: '100x100' },
  { widthMm: 75, heightMm: 120, title: '75x120' },
  { widthMm: 100, heightMm: 150, title: '100x150' },
] as const;

export const PRINT_PROFILES = [
  { id: 'thermal-203', label: 'Термо 203 DPI', dpi: 203 as const, safeMarginMm: 1, density: 0 },
  { id: 'thermal-300', label: 'Термо 300 DPI', dpi: 300 as const, safeMarginMm: 1, density: 0 },
  { id: 'thermal-203-tight', label: 'Термо 203 DPI (плотно)', dpi: 203 as const, safeMarginMm: 0.5, density: 10 },
  { id: 'thermal-300-fine', label: 'Термо 300 DPI (точно)', dpi: 300 as const, safeMarginMm: 0.5, density: 5 },
] as const;

/** Пресет по умолчанию (203 DPI) — новый шаблон и начальное состояние редактора. */
export const DEFAULT_PRINT_PROFILE_ID = 'thermal-203' as const satisfies (typeof PRINT_PROFILES)[number]['id'];

/** Подсказка для иконки «?» рядом с полем «Профиль печати». */
export const PRINT_PROFILE_HINT =
  'Профиль печати задаёт разрешение принтера (DPI) и красную зону безопасных полей на холсте. От этого зависят размер этикетки в пикселях, чёткость штрихкодов и качество экспорта в PNG/PDF. Выберите пресет под ваш термопринтер или «Свой», чтобы донастроить отступ и плотность.';

export const FIELD_LIBRARY: FieldCatalogSeed[] = [
  { label: 'Штрихкод', type: 'barcode', binding: 'label.barcode', width: 35, height: 10 },
  { label: 'QR-код', type: 'qr', binding: 'label.number', width: 18, height: 18 },
  { label: 'DataMatrix / КИЗ', type: 'datamatrix', binding: 'label.serial', width: 20, height: 20 },
  { label: 'Текстовое поле Честного знака', type: 'text', binding: 'label.honestSignText', width: 36, height: 5, showLabel: false },
  { label: 'Название товара', type: 'text', binding: 'label.name', width: 50, height: 6 },
  { label: 'Наименование продавца', type: 'text', binding: 'label.sellerName', width: 45, height: 5, showLabel: true },
  { label: 'Короткое наименование продавца', type: 'text', binding: 'label.sellerShortName', width: 45, height: 5, showLabel: true },
  { label: 'Идентификатор печатающего', type: 'text', binding: 'label.userId', width: 45, height: 5, showLabel: true },
  { label: 'Артикул товара', type: 'text', binding: 'label.article', width: 40, height: 5, showLabel: true },
  { label: 'Артикул продавца', type: 'text', binding: 'label.sellerArticle', width: 45, height: 5, showLabel: true },
  { label: 'Размер', type: 'text', binding: 'label.size', width: 20, height: 5, showLabel: true },
  { label: 'Рос. размер', type: 'text', binding: 'label.techSize', width: 24, height: 5, showLabel: true },
  { label: 'Размер + Рос. размер', type: 'text', binding: 'label.sizeRuCombined', width: 28, height: 5, showLabel: false },
  { label: 'Цвет', type: 'text', binding: 'label.color', width: 25, height: 5, showLabel: true },
  { label: 'Бренд', type: 'text', binding: 'label.brand', width: 25, height: 5, showLabel: true },
  { label: 'Состав', type: 'text', binding: 'label.composition', width: 52, height: 6 },
  { label: 'Страна производства', type: 'text', binding: 'label.country', width: 35, height: 5, showLabel: true },
  { label: 'Производитель', type: 'text', binding: 'label.manufacturer', width: 35, height: 5, showLabel: true },
  { label: 'Дата производства', type: 'text', binding: 'label.manufactureDate', width: 30, height: 5, showLabel: true },
  { label: 'GTIN (Честный знак)', type: 'text', binding: 'label.gtin', width: 34, height: 5, showLabel: true },
  { label: 'Серийный номер (Честный знак)', type: 'text', binding: 'label.serial', width: 36, height: 5, showLabel: true },
  { label: 'Короткий адрес', type: 'text', binding: 'label.shortAddress', width: 40, height: 5, showLabel: true },
  { label: 'Адрес', type: 'text', binding: 'label.address', width: 44, height: 5, showLabel: true },
  { label: 'Номер этикетки', type: 'text', binding: 'label.number', width: 24, height: 5, showLabel: false },
];

/**
 * Режим превью «Склад»: те же привязки label.*, что в демо ячейки/ЕХ,
 * подписи в каталоге — под адрес и единицу хранения, не под товарный SKU.
 */
export const WAREHOUSE_CELL_FIELD_LIBRARY: FieldCatalogSeed[] = [
  { group: 'Коды', label: 'Штрихкод (payload ячейки)', type: 'barcode', binding: 'label.barcode', width: 35, height: 10 },
  { group: 'Коды', label: 'QR-код (адрес латиница)', type: 'qr', binding: 'label.addressLatin', width: 18, height: 18 },
  { group: 'Коды', label: 'DataMatrix / код ЕХ', type: 'datamatrix', binding: 'label.serial', width: 20, height: 20 },
  { group: 'Подпись и адрес', label: 'Подпись ячейки', type: 'text', binding: 'label.name', width: 50, height: 6 },
  { group: 'Подпись и адрес', label: 'Короткий адрес', type: 'text', binding: 'label.shortAddress', width: 28, height: 5, showLabel: true },
  { group: 'Подпись и адрес', label: 'Адрес (латиница)', type: 'text', binding: 'label.addressLatin', width: 32, height: 5, showLabel: true },
  {
    group: 'Подпись и адрес',
    label: 'Адрес (формат склада)',
    type: 'locationStrip',
    binding: 'label.color',
    width: 52,
    height: 14,
    showLabel: false,
  },
  { group: 'Подпись и адрес', label: 'Адрес комбинированный', type: 'text', binding: 'label.sizeRuCombined', width: 28, height: 5, showLabel: false },
  { group: 'Подпись и адрес', label: 'Зона / ряд / ячейка', type: 'text', binding: 'label.color', width: 30, height: 5, showLabel: true },
  { group: 'Склад и единица хранения', label: 'Код склада', type: 'text', binding: 'label.article', width: 26, height: 5, showLabel: true },
  { group: 'Склад и единица хранения', label: 'Код единицы хранения', type: 'text', binding: 'label.sellerArticle', width: 36, height: 5, showLabel: true },
  { group: 'Склад и единица хранения', label: 'Название склада', type: 'text', binding: 'label.brand', width: 40, height: 5, showLabel: true },
  { group: 'Склад и единица хранения', label: 'Учётный идентификатор', type: 'text', binding: 'label.userId', width: 30, height: 5, showLabel: true },
  {
    group: 'Группы хранения',
    label: 'Группы хранения',
    type: 'text',
    binding: 'label.storageGroup',
    width: 36,
    height: 5,
    showLabel: true,
  },
  { group: 'Дополнительно', label: 'Описание ячейки', type: 'text', binding: 'label.composition', width: 52, height: 6 },
  { group: 'Дополнительно', label: 'Страна', type: 'text', binding: 'label.country', width: 22, height: 5, showLabel: true },
  { group: 'Дополнительно', label: 'Производитель / система', type: 'text', binding: 'label.manufacturer', width: 36, height: 5, showLabel: true },
  { group: 'Дополнительно', label: 'Дата', type: 'text', binding: 'label.manufactureDate', width: 28, height: 5, showLabel: true },
  { group: 'Дополнительно', label: 'Payload (текстом)', type: 'text', binding: 'label.number', width: 38, height: 5, showLabel: false },
];

const DEFAULT_DPI = 203;

function pxToMmAtDefaultDpi(px: number): number {
  return (px * 25.4) / DEFAULT_DPI;
}

function compactTextSizeMm(label: string, value: string, showLabel: boolean, fontSize: number, lineHeight: number) {
  const text = (showLabel ? `${label}: ${value || ''}` : value || label).trim() || label;
  // Simple text metric approximation for initial box size.
  const widthPx = Math.max(fontSize * 2, text.length * fontSize * 0.58 + 8);
  const heightPx = Math.max(fontSize * lineHeight + 4, fontSize + 4);
  return {
    width: Math.max(5, Number(pxToMmAtDefaultDpi(widthPx).toFixed(1))),
    height: Math.max(2, Number(pxToMmAtDefaultDpi(heightPx).toFixed(1))),
  };
}

/** Размер блока «Название товара»: ширина по длине текста (до maxWidthMm), высота с учётом переноса по словам. */
function productNameWrappedSizeMm(
  resolvedText: string,
  fontSize: number,
  lineHeight: number,
  maxWidthMm: number,
): { width: number; height: number } {
  const text = resolvedText.trim() || ' ';
  const charWidthPx = fontSize * 0.58;
  const singleLineWidthPx = Math.max(fontSize * 2, text.length * charWidthPx + 8);
  const singleLineWidthMm = Math.max(5, Number(pxToMmAtDefaultDpi(singleLineWidthPx).toFixed(1)));
  const widthMm = Math.min(Math.max(8, singleLineWidthMm), maxWidthMm);

  const boxWidthPx = (widthMm * DEFAULT_DPI) / 25.4;
  const charsPerLine = Math.max(1, Math.floor(boxWidthPx / charWidthPx));
  const lines = Math.max(1, Math.ceil(text.length / charsPerLine));

  const heightPx = lines * fontSize * lineHeight + 6;
  const heightMm = Math.max(3, Number(pxToMmAtDefaultDpi(heightPx).toFixed(1)));
  return { width: widthMm, height: heightMm };
}

function initialBarcodeContainerHeightMm(): number {
  const barcodeHeight = 10;
  const barcodeTextFontSize = 14;
  const barcodeTextLineHeight = 0;
  const textGapPx = 10;
  const textHeightPx = Math.ceil(Math.max(1, barcodeTextFontSize) * Math.max(1, barcodeTextLineHeight || 1.2));
  const reservedTextMm = pxToMmAtDefaultDpi(textGapPx + textHeightPx);
  const value = barcodeHeight + reservedTextMm;
  return Math.max(2, Math.round((value + Number.EPSILON) * 10) / 10);
}

export function getByBinding(binding: string, data: LabelPreviewData): string {
  return binding.split('.').reduce<unknown>((acc, key) => {
    if (typeof acc !== 'object' || acc === null) return '';
    return (acc as Record<string, unknown>)[key];
  }, data) as string || '';
}

export function createDefaultTemplate(): LabelTemplate {
  const defaultDpi = PRINT_PROFILES.find((p) => p.id === DEFAULT_PRINT_PROFILE_ID)?.dpi ?? 203;
  return {
    id: 'template_58x40_default',
    name: 'Этикетка 58x40',
    paperType: 'thermal',
    labelSize: { widthMm: 58, heightMm: 40 },
    elements: [],
    dpi: defaultDpi,
  };
}

export function getTemplateValueByBinding(binding: string, type: LabelElementType): string {
  if (type === 'datamatrix') return '{{gtin}}{{serial}}';
  if (binding === 'label.honestSignText') return '{{gtin}}\n{{serial}}';
  if (binding === 'label.sizeRuCombined') return '{{size}}-{{techSize}}';
  const token = bindingToToken(binding);
  return token || '';
}

export function createElement(seed: FieldCatalogSeed, value: string, layoutPreview?: string): LabelElement {
  const defaultFontSize = 14;
  const defaultLineHeight = 1.2;
  const isSizeRuCombined = seed.binding === 'label.sizeRuCombined';
  const isHonestSignTextField = seed.binding === 'label.honestSignText';
  const effectiveFontSize = defaultFontSize;
  const effectiveLineHeight = defaultLineHeight;
  const textSize = seed.type === 'text'
    ? seed.binding === 'label.name' && layoutPreview
      ? productNameWrappedSizeMm(layoutPreview, effectiveFontSize, effectiveLineHeight, seed.width)
      : compactTextSizeMm(seed.label, value, seed.showLabel ?? false, effectiveFontSize, effectiveLineHeight)
    : null;
  const codeMm: DataMatrixCodeSizeMm =
    seed.type === 'datamatrix' ? snapDataMatrixCodeSizeMm(seed.width) : 25;
  const elementId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? `${seed.type}_${crypto.randomUUID().slice(0, 8)}`
      : `${seed.type}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

  const widthMm =
    seed.type === 'datamatrix'
      ? codeMm
      : seed.type === 'barcode'
        ? 35
        : seed.type === 'locationStrip'
          ? seed.width
          : seed.type === 'text' && textSize
            ? textSize.width
            : seed.width;
  const heightMm =
    seed.type === 'datamatrix'
      ? codeMm
      : seed.type === 'barcode'
        ? initialBarcodeContainerHeightMm()
        : isHonestSignTextField
          ? seed.height
          : seed.type === 'locationStrip'
            ? seed.height
            : seed.type === 'text' && textSize
              ? textSize.height
              : seed.height;

  const rotation = 0 as RotationType;

  switch (seed.type) {
    case 'text':
      return {
        id: elementId,
        type: 'text',
        label: seed.label,
        binding: seed.binding,
        x: 4,
        y: 4,
        width: widthMm,
        height: heightMm,
        rotation,
        value,
        fontSize: isSizeRuCombined ? 22 : effectiveFontSize,
        lineHeight: effectiveLineHeight,
        letterSpacing: isSizeRuCombined ? 3 : 0,
        fontWeight: isSizeRuCombined ? 'bold' : 'normal',
        fontStyle: 'normal',
        textDecoration: 'none',
        align: 'left',
        showLabel: seed.showLabel ?? false,
        wrap: true,
      };
    case 'barcode':
      return {
        id: elementId,
        type: 'barcode',
        label: seed.label,
        binding: seed.binding,
        x: 4,
        y: 4,
        width: widthMm,
        height: heightMm,
        rotation,
        value,
        barcodeWidth: 35,
        barcodeHeight: 10,
        barcodePaddingTop: 0,
        barcodePaddingLeft: 0,
        barcodePaddingBottom: 0,
        barcodePaddingRight: 0,
        barcodeAlign: 'left',
        barcodeTextFontSize: 14,
        barcodeTextLineHeight: 0,
        barcodeTextLetterSpacing: 0,
      };
    case 'qr':
      return {
        id: elementId,
        type: 'qr',
        label: seed.label,
        binding: seed.binding,
        x: 4,
        y: 4,
        width: widthMm,
        height: heightMm,
        rotation,
        value,
      };
    case 'datamatrix':
      return {
        id: elementId,
        type: 'datamatrix',
        label: seed.label,
        binding: seed.binding,
        x: 4,
        y: 4,
        width: widthMm,
        height: heightMm,
        rotation,
        value,
        datamatrixVisible: true,
        datamatrixSize: 0,
        datamatrixCodeSizeMm: codeMm,
        datamatrixPaddingTop: 5,
        datamatrixPaddingLeft: 0,
        datamatrixPaddingBottom: 5,
        datamatrixPaddingRight: 0,
        datamatrixCornerMarkers: true,
        datamatrixMode: 'test',
        datamatrixManualValue: false,
      };
    case 'svg':
      return {
        id: elementId,
        type: 'svg',
        label: seed.label,
        binding: seed.binding,
        x: 4,
        y: 4,
        width: widthMm,
        height: heightMm,
        rotation,
        value,
        svgVisible: true,
        svgPaddingTop: 5,
        svgPaddingLeft: 0,
        svgPaddingBottom: 5,
        svgPaddingRight: 0,
        svgAlign: 'left',
      };
    case 'locationStrip':
      return {
        id: elementId,
        type: 'locationStrip',
        label: seed.label,
        binding: seed.binding,
        x: 4,
        y: 4,
        width: widthMm,
        height: heightMm,
        rotation,
        value,
        fontSize: 11,
        lineHeight: effectiveLineHeight,
        letterSpacing: 0,
        fontWeight: 'normal',
        fontStyle: 'normal',
        textDecoration: 'none',
        align: 'left',
        showLabel: seed.showLabel ?? false,
        wrap: true,
        locationStripLabelZone: 'Зона',
        locationStripLabelRow: 'Ряд',
        locationStripLabelCell: 'Ячейка',
        locationStripLabelLevel: 'Уровень',
        locationStripCaptionFontSize: 9,
      };
    case 'czLogo':
    case 'eacLogo':
      return {
        id: elementId,
        type: seed.type,
        label: seed.label,
        binding: seed.binding,
        x: 4,
        y: 4,
        width: widthMm,
        height: heightMm,
        rotation,
        value,
      };
    default:
      return {
        id: elementId,
        type: 'text',
        label: seed.label,
        binding: seed.binding,
        x: 4,
        y: 4,
        width: widthMm,
        height: heightMm,
        rotation,
        value,
        fontSize: effectiveFontSize,
        lineHeight: effectiveLineHeight,
        letterSpacing: 0,
        fontWeight: 'normal',
        fontStyle: 'normal',
        textDecoration: 'none',
        align: 'left',
        showLabel: seed.showLabel ?? false,
        wrap: true,
      };
  }
}
